import React, { forwardRef } from 'react';
import { cn } from 'src/utils/className';

type ButtonProps = {
  children: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'default';
  size?: 'small' | 'medium' | 'large';
  fontWeight?: 'light' | 'regular' | 'medium' | 'bold';
  onClick?: (evt: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  href?: string;
  target?: string;
  rel?: string;
};

const variantClasses = {
  contained: {
    primary:
      'bg-primary text-neutral-0 hover:bg-primary/90 active:bg-primary/80',
    secondary:
      'bg-neutral-400 text-neutral-0 hover:bg-neutral-500 active:bg-neutral-600',
    error: 'bg-error text-neutral-0 hover:bg-error-500 active:bg-error-600',
    default:
      'bg-neutral-400 text-neutral-0 hover:bg-neutral-500 active:bg-neutral-600',
  },
  outlined: {
    primary:
      'border border-primary text-primary hover:bg-neutral-50 active:bg-neutral-100',
    secondary:
      'border-2 border-neutral-400 text-neutral-400 hover:bg-neutral-50 active:bg-neutral-100',
    error:
      'border border-error text-error hover:text-neutral-0 hover:bg-error-500 active:bg-error-600',
    default:
      'border-2 border-neutral-400 text-neutral-400 hover:bg-neutral-50 active:bg-neutral-100',
  },
  text: {
    primary: 'text-primary hover:bg-primary/10 active:bg-primary/20',
    secondary: 'text-neutral-400 hover:bg-neutral-50 active:bg-neutral-100',
    error: 'text-error hover:bg-error-500 active:bg-error-600',
    default: 'text-neutral-400 hover:bg-neutral-50 active:bg-neutral-100',
  },
};

const sizeClasses = {
  small: 'px-3 py-2 text-sm',
  medium: 'px-4 py-3 text-base',
  large: 'px-5 py-4 text-lg',
};

const fontWeightClasses = {
  light: 'font-[300]',
  regular: 'font-[400]',
  medium: 'font-[500]',
  bold: 'font-[700]',
};

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      children,
      variant = 'contained',
      color = 'primary',
      size = 'medium',
      fontWeight = 'regular',
      onClick,
      className,
      type = 'button',
      disabled = false,
      href = undefined,
      target = undefined,
      rel = undefined,
      ...rest
    }: ButtonProps,
    ref,
  ) => {
    const variantClass = variantClasses[variant][color];
    const sizeClass = sizeClasses[size];
    const weightClass = fontWeightClasses[fontWeight];

    const baseClasses = cn(
      'inline-flex items-center justify-center',
      'rounded-lg transition-all duration-200',
      !disabled && variantClass,
      sizeClass,
      weightClass,
      disabled &&
        'bg-neutral-50 text-neutral-400 cursor-not-allowed pointer-events-none',
      className,
    );

    if (href) {
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={disabled ? undefined : href}
          target={target}
          rel={rel || 'noreferrer'}
          className={baseClasses}
          {...rest}
        >
          {children}
        </a>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={baseClasses}
        onClick={onClick}
        type={type}
        disabled={disabled}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
